import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Skeleton, Typography, Chip, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RequestLeaveModal from '../../../components/RequestLeaveModal';
import ApproveLeaveModal from '../../../components/ApproveLeaveModal';
import LeaveDetailDrawer from '../../../components/LeaveDetailDrawer';
import ExportFormatModal from '../../../components/ExportFormatModal';
import { StaffService } from '../../../api/services/staffService';
import { exportClientSide } from '../../../utils/exportUtils';
import { extractTotalCount, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function LeaveRequestsTab({
    exportToCSV,
    handleSuccess,
    setNotification
}) {
    const { user: currentUser } = useSelector((state) => state.auth);
    const [localLeaveRequests, setLocalLeaveRequests] = useState([]);
    const [totalLeaveRequests, setTotalLeaveRequests] = useState(0);
    const [localIsLoading, setLocalIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isRequestLeaveOpen, setIsRequestLeaveOpen] = useState(false);
    const [isApproveLeaveOpen, setIsApproveLeaveOpen] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [leaveAction, setLeaveAction] = useState('Approved');
    const [isLeaveDetailOpen, setIsLeaveDetailOpen] = useState(false);
    const [selectedLeaveId, setSelectedLeaveId] = useState(null);
    const [isCancelLeaveOpen, setIsCancelLeaveOpen] = useState(false);
    const [leaveToCancel, setLeaveToCancel] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const fetchPaginatedLeaves = async () => {
        setLocalIsLoading(true);
        try {
            const data = await StaffService.getLeaveRequests({ page, limit: rowsPerPage });
            const dataArray = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
            setLocalLeaveRequests(dataArray);
            setTotalLeaveRequests(extractTotalCount(data, dataArray));
        } catch (error) {
            console.error("Failed to fetch paginated leaves:", error);
        } finally {
            setLocalIsLoading(false);
        }
    };

    React.useEffect(() => {
        fetchPaginatedLeaves();
    }, [page, rowsPerPage]);

    const handleOpenLeaveAction = (leaveRequest, action) => {
        setSelectedLeave(leaveRequest);
        setLeaveAction(action);
        setIsApproveLeaveOpen(true);
    };

    const handleLeaveActionSuccess = (message, updatedId, newStatus) => {
        fetchPaginatedLeaves();
        handleSuccess(message); // triggers parent refresh for metrics
    };

    const handleCancelLeave = (leaveRequest) => {
        setLeaveToCancel(leaveRequest);
        setIsCancelLeaveOpen(true);
    };

    const handleConfirmCancel = async () => {
        if (!leaveToCancel) return;
        setIsCancelling(true);
        try {
            await StaffService.cancelLeaveRequest(leaveToCancel.id || leaveToCancel._id);
            fetchPaginatedLeaves();
            handleSuccess('Leave request cancelled successfully');
            setIsCancelLeaveOpen(false);
            setLeaveToCancel(null);
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Failed to cancel leave request.';
            setNotification({ open: true, message: msg, severity: 'error' });
        } finally {
            setIsCancelling(false);
        }
    };

    const handleExport = (format) => {
        setExportModalOpen(false);
        try {
            exportClientSide(format, localLeaveRequests, 'leave_requests');
            handleSuccess('Export successful.');
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Export failed.', severity: 'error' });
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
                <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />} sx={standardSecondaryButtonSx} onClick={() => setExportModalOpen(true)}>
                    Export Options
                </Button>
                <ExportFormatModal
                    open={exportModalOpen}
                    onClose={() => setExportModalOpen(false)}
                    onExport={handleExport}
                    isExporting={false}
                    title="Export Leave Requests"
                />
                {currentUser?.isStaff && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsRequestLeaveOpen(true)}>
                        Apply for Leave
                    </Button>
                )}
            </Box>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Staff Member</TableCell>
                            <TableCell>Leave Type</TableCell>
                            <TableCell>Start Date</TableCell>
                            <TableCell>End Date</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {localIsLoading ? (
                            [...Array(5)].map((_, index) => (
                                <TableRow key={`skeleton-leave-${index}`}>
                                    <TableCell colSpan={7}><Skeleton variant="rectangular" height={40} sx={{ my: 1, borderRadius: 1 }} /></TableCell>
                                </TableRow>
                            ))
                        ) : localLeaveRequests.length > 0 ? (
                            localLeaveRequests.map((l) => {
                                const isSelf = currentUser && (
                                    (l.staffId && (
                                        (currentUser.id && l.staffId === currentUser.id) || 
                                        (currentUser._id && l.staffId === currentUser._id) || 
                                        (currentUser.staffId && l.staffId === currentUser.staffId)
                                    )) ||
                                    (l.staff && (
                                        (l.staff.id && currentUser.id && l.staff.id === currentUser.id) || 
                                        (l.staff._id && currentUser.id && l.staff._id === currentUser.id) || 
                                        (l.staff.id && currentUser._id && l.staff.id === currentUser._id) || 
                                        (l.staff._id && currentUser._id && l.staff._id === currentUser._id)
                                    )) ||
                                    (l.userId && (
                                        (currentUser.id && l.userId === currentUser.id) || 
                                        (currentUser._id && l.userId === currentUser._id)
                                    ))
                                );
                                
                                return (
                                <TableRow key={l.id || l._id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{l.staff?.fullName || l.staffName || l.staff || 'Unknown'}</Typography>
                                        {l.staff?.employeeId && <Typography variant="caption" color="text.secondary">{l.staff.employeeId}</Typography>}
                                    </TableCell>
                                    <TableCell>{l.leaveType || l.type || '--'}</TableCell>
                                    <TableCell>{l.startDate || l.dates || '--'}</TableCell>
                                    <TableCell>{l.endDate || '--'}</TableCell>
                                    <TableCell sx={{ maxWidth: 200 }}>
                                        <Typography variant="body2" noWrap title={l.reason}>{l.reason || '--'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={l.status || 'Pending'} 
                                            size="small" 
                                            color={l.status === 'Approved' ? 'success' : l.status === 'Rejected' ? 'error' : 'warning'} 
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        {l.status === 'Pending' ? (
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                <Button size="small" color="primary" variant="outlined" onClick={() => handleOpenLeaveAction(l, 'Approved')} disabled={isSelf}>Approve</Button>
                                                <Button size="small" color="error" variant="outlined" onClick={() => handleOpenLeaveAction(l, 'Rejected')} disabled={isSelf}>Reject</Button>
                                                <Button size="small" color="warning" variant="outlined" onClick={() => handleCancelLeave(l)} disabled={!isSelf}>Cancel</Button>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                                                <Chip
                                                    label={l.status === 'Approved' ? 'Approved ✓' : 'Rejected ✗'}
                                                    size="small"
                                                    variant="outlined"
                                                    color={l.status === 'Approved' ? 'success' : 'error'}
                                                />
                                                <Button size="small" onClick={() => { setSelectedLeaveId(l.id || l._id); setIsLeaveDetailOpen(true); }}>View</Button>
                                            </Box>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )})
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">No leave requests found.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalLeaveRequests}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>

            <RequestLeaveModal
                open={isRequestLeaveOpen}
                onClose={() => setIsRequestLeaveOpen(false)}
                onSuccess={handleSuccess}
            />

            <ApproveLeaveModal
                open={isApproveLeaveOpen}
                onClose={() => { setIsApproveLeaveOpen(false); setSelectedLeave(null); }}
                leaveRequest={selectedLeave}
                action={leaveAction}
                onSuccess={handleLeaveActionSuccess}
            />

            <LeaveDetailDrawer
                open={isLeaveDetailOpen}
                onClose={() => { setIsLeaveDetailOpen(false); setSelectedLeaveId(null); }}
                leaveId={selectedLeaveId}
            />

            <Dialog open={isCancelLeaveOpen} onClose={isCancelling ? undefined : () => setIsCancelLeaveOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Cancel Leave Request</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to cancel this leave request?
                    </Typography>
                    {leaveToCancel && (
                        <Box sx={{ mt: 2, bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                            <Typography variant="body2"><b>Type:</b> {leaveToCancel.leaveType || leaveToCancel.type}</Typography>
                            <Typography variant="body2"><b>Dates:</b> {leaveToCancel.startDate} → {leaveToCancel.endDate}</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 1 }}>
                    <Button onClick={() => setIsCancelLeaveOpen(false)} disabled={isCancelling}>No, Keep It</Button>
                    <Button onClick={handleConfirmCancel} color="error" variant="contained" disabled={isCancelling}>
                        {isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
